import 'react-table/react-table.css';
import './index.scss';

import createBrowserHistory from 'history/createBrowserHistory';
import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { Provider } from 'react-redux';
import { createForms } from 'react-redux-form';
import { Router } from 'react-router';
// import { HashRouter } from 'react-router-dom';
import { applyMiddleware, combineReducers, createStore } from 'redux';
import { composeWithDevTools } from 'redux-devtools-extension';
import thunk from 'redux-thunk';
import { readCookie } from '@common/cookies';
import { Timer } from '@common/timer';
// import registerServiceWorker from './registerServiceWorker';

// Models
import { LoginViewModel, ProfileViewModel } from '@models/Auth';
import { authService } from '@services/AuthService';
import { CustomerViewModel } from '@models/Customer';
import { EscrowOfficerViewModel } from '@models/EscrowOfficer';
import { EscrowOfficeViewModel } from '@models/EscrowOffice';
import { RealEstateAgentViewModel } from '@models/RealEstateAgent';
import { RealEstateOfficeViewModel } from '@models/RealEstateOffice';
import { TransactionCoordinatorViewModel } from '@models/TransactionCoordinator';
import { utilityService } from '@services/UtilityService';
import { PropertyManagerViewModel } from '@models/PropertyManager';
import { VendorViewModel } from '@models/Vendor';
import { AddressViewModel, OptionsViewModel } from '@models/common.models';

// Components
import Forms from '@constants/Forms';
import App from './App/App';
import { ToastComponent, ToastType, ToastOptions } from '@common/components';
import { toast } from 'react-toastify';
import { config } from '@common/config';

// Reducers
import { AuthReducer, AuthState } from './auth';
import { logout, timerExpired, loginSuccess, timerResetCallback, timerSet } from './auth/auth.actions';
import { CilOfferReducer, CilOfferState } from './cil-offer/cil-offer.reducer';
import { CommonReducer, CommonState } from '@common/components/common.reducer';
import { CustomerReducer, CustomerState } from './customer/customer.reducer';
import { EscrowOfficeReducer, EscrowOfficeState } from './escrow-office/escrow-office.reducer';
import { EscrowOfficerReducer, EscrowOfficerState } from './escrow-officer/escrow-officer.reducer';
import { ProfileReducer, ProfileState } from './profile/profile.reducer';
import { RealEstateAgentReducer, RealEstateAgentState } from './realestate-agent/realestate-agent.reducer';
import { RealEstateOfficeReducer, RealEstateOfficeState } from './realestate-office/realestate-office.reducer';
import {
  TransactionCoordinatorReducer,
  TransactionCoordinatorState
} from 'src/transaction-coordinator/transaction-coordinator.reducer';
import { UserReducer, UserState } from './user/user.reducer';
import { UsersReducer, UsersState } from './users/users.reducer';
import { ContractReducer, ContractState } from './contract/contract.reducer';
import { CILNotificationReducer, CILNotificationState } from './cil-notification/cil-notification.reducer';
// import { initialPaymentViewModel } from './contract/_components/payment/payment.component';
import { AutoAuthoReducer, AutoAuthoState } from './auto-autho/auto-autho.reducer';
import { DynamicSelectState, DynamicSelectReducer } from '@common/components/dynamic-select/dynamic-select.reducer';
import { QuoteState, QuoteReducer } from './quote/quote.reducer';
import { CommentState, CommentReducer } from './comments/comments.reducer';
import {
  PropertyManagementState,
  PropertyManagementReducer
} from 'src/property-management/property-management.reducer';
import { PropertyManagerReducer, PropertyManagerState } from 'src/property-manager/property-manager.reducer';
import { FileUploadForm } from '@common/components/file-upload/_components/file-upload.component';

import { VendorReducer, VendorState } from './vendor/vendor.reducer';

import {
  ContractTransferReducer,
  ContractTransferState
} from './contract/_components/contract-transfer/contract-transfer.reducer';
import { ServiceWorkOrderState, ServiceWorkOrderReducer } from './service-work-order/service-work-order.reducer';
import { ServiceWorkOrderViewModel } from '@models/ServiceWorkOrder';
import { CVIReducer, CVIState } from './service-work-order/cvi/cvi.reducer';
import { DispatchState, DispatchReducer } from './service-work-order/dispatch/dispatch.reducer';
import { ZeroVendorState, ZeroVendorReducer } from './service-work-order/zero-vendor/zero-vendor.reducer';
import { FailedEmailState, FailedEmailReducer } from './failed-email/failed-email.reducer';
import { CoverageState, CoverageReducer } from './contract/_components/coverage/coverage.reducer';
import { AuthorizationState, AuthorizationReducer } from './authorization/authorization.reducer';
import { InvoicingState, InvoicingReducer } from './invoicing/invoicing.reducer';
import { PurchasingState, PurchasingReducer } from './purchasing/purchasing.reducer';
import { PaymentReducer, PaymentState } from './payment/payment.reducer';
import { AccountingState, AccountingReducer } from 'src/accounting/accounting.reducer';
import { CreditCardState, CreditCardReducer } from '@common/components/credit-cards/credit-card.reducer';
import { ActioningState, ActioningReducer } from 'src/actioning/actioning.reducer';
import { RouteDefinitions } from '@common/route-definitions';
import {
  VendorDashboardState,
  VendorMaintenanceReducer
} from './vendor/_components/vendor-dashboard/vendor-dashboard.reducer';
import {
  PurchaseRequestInfoViewModel,
  PurchaseRequestHvacViewModel,
  PurchaseRequestPartsViewModel
} from '@models/PurchaseRequest';
import { DocumentHistoryState, DocumentHistoryReducer } from './document-history/document-history.reducer';
import FormNames from '@constants/FormNames.enum';
import { ContractEmailForm, PropertyTypeViewModel } from '@models/Contract';
import { ContractErrorState, ContractErrorReducer } from 'src/contract-error/contract-error.reducer';
// import FormNames from '@constants/FormNames.enum';

export interface AppState {
  readonly accounting: AccountingState;
  readonly actioning: ActioningState;
  readonly auth: AuthState;
  readonly authorization: AuthorizationState;
  readonly autoAutho: AutoAuthoState;
  readonly cilNotification: CILNotificationState;
  readonly comment: CommentState;
  readonly common: CommonState;
  readonly contract: ContractState;
  readonly contractError: ContractErrorState;
  readonly contractTransfer: ContractTransferState;
  readonly coverageStep: CoverageState;
  readonly creditCards: CreditCardState;
  readonly cilOffer: CilOfferState;
  readonly customer: CustomerState;
  readonly customerForm: CustomerViewModel;
  readonly cviState: CVIState;
  readonly dispatchState: DispatchState;
  readonly zeroVendorState: ZeroVendorState;
  readonly failedEmailState: FailedEmailState;
  readonly documentHistory: DocumentHistoryState;
  readonly dynamicSelect: DynamicSelectState;
  readonly escrowOffice: EscrowOfficeState;
  readonly escrowOfficeForm: EscrowOfficeViewModel;
  readonly escrowOfficer: EscrowOfficerState;
  readonly escrowOfficerForm: EscrowOfficerViewModel;
  readonly fileUploadForm: FileUploadForm;
  readonly invoicing: InvoicingState;
  readonly loginForm: LoginViewModel;
  readonly payment: PaymentState;
  readonly profile: ProfileState;
  readonly profileForm: ProfileViewModel;
  readonly propertyInfoForm: {
    contactName: string;
    contactType: OptionsViewModel;
    contactMedium: OptionsViewModel;
    contactInit: OptionsViewModel;
    propertyAddress: AddressViewModel;
    propertyType: PropertyTypeViewModel;
    yearBuilt: string;
    propertySaleType: OptionsViewModel;
  };
  readonly propertyManagement: PropertyManagementState;
  readonly propertyManager: PropertyManagerState;
  readonly propertyManagerForm: PropertyManagerViewModel;
  readonly purchasing: PurchasingState;
  readonly [FormNames.PurchaseApplianceInfoForm]: PurchaseRequestInfoViewModel;
  readonly [FormNames.PurchaseHvacForm]: PurchaseRequestInfoViewModel;
  readonly [FormNames.PurchaseHvacItemForm]: PurchaseRequestHvacViewModel;
  readonly [FormNames.PurchasePartsForm]: PurchaseRequestInfoViewModel;
  readonly [FormNames.PurchasePartsItemForm]: PurchaseRequestPartsViewModel;
  readonly [FormNames.ContractEmailForm]: ContractEmailForm;
  readonly quote: QuoteState;
  readonly realEstateAgent: RealEstateAgentState;
  readonly realEstateAgentForm: RealEstateAgentViewModel;
  readonly realEstateOffice: RealEstateOfficeState;
  readonly realEstateOfficeForm: RealEstateOfficeViewModel;
  readonly serviceWorkOrder: ServiceWorkOrderState;
  readonly serviceWorkOrderForm: ServiceWorkOrderViewModel;
  readonly transactionCoordinator: TransactionCoordinatorState;
  readonly transactionCoordinatorForm: TransactionCoordinatorViewModel;
  readonly user: UserState;
  readonly users: UsersState;
  readonly vendor: VendorState;
  readonly vendorForm: VendorViewModel;
  readonly vendorMaintenance: VendorDashboardState;
  readonly forms: { [prop: string]: any };
}

export const store = createStore<AppState>(
  combineReducers({
    ...createForms({ ...Forms }),
    accounting: AccountingReducer,
    actioning: ActioningReducer,
    auth: AuthReducer,
    authorization: AuthorizationReducer,
    autoAutho: AutoAuthoReducer,
    cilNotification: CILNotificationReducer,
    comment: CommentReducer,
    common: CommonReducer,
    contract: ContractReducer,
    contractError: ContractErrorReducer,
    contractTransfer: ContractTransferReducer,
    coverageStep: CoverageReducer,
    creditCards: CreditCardReducer,
    cilOffer: CilOfferReducer,
    customer: CustomerReducer,
    cviState: CVIReducer,
    dispatchState: DispatchReducer,
    zeroVendorState: ZeroVendorReducer,
    failedEmailState: FailedEmailReducer,
    documentHistory: DocumentHistoryReducer,
    dynamicSelect: DynamicSelectReducer,
    escrowOffice: EscrowOfficeReducer,
    escrowOfficer: EscrowOfficerReducer,
    invoicing: InvoicingReducer,
    payment: PaymentReducer,
    profile: ProfileReducer,
    propertyManagement: PropertyManagementReducer,
    propertyManager: PropertyManagerReducer,
    purchasing: PurchasingReducer,
    quote: QuoteReducer,
    realEstateAgent: RealEstateAgentReducer,
    realEstateOffice: RealEstateOfficeReducer,
    serviceWorkOrder: ServiceWorkOrderReducer,
    transactionCoordinator: TransactionCoordinatorReducer,
    user: UserReducer,
    users: UsersReducer,
    vendor: VendorReducer,
    vendorMaintenance: VendorMaintenanceReducer
  }),
  composeWithDevTools(applyMiddleware(thunk))
);

export const timer = new Timer(
  () => store.dispatch(logout()),
  () => store.dispatch(timerExpired()),
  () => store.dispatch(timerResetCallback()),
  (time: string) => store.dispatch(timerSet(time))
);

interface PageHit {
  timestamp: number;
  pathname: string;
}

let lastPageHit: PageHit | null = null;

export const logPageDuration = async () => {
  if (lastPageHit) {
    try {
      const duration = Date.now() - lastPageHit.timestamp;
      await utilityService.logPage(lastPageHit.pathname, duration);
    } catch (e) {
      toast.error(<ToastComponent type={ToastType.Error} message={e.message} />, ToastOptions.Error);
    }
  }
};

export const history = createBrowserHistory();

history.location.state = {};

history.listen(async (location, action) => {
  if (location.pathname === RouteDefinitions.login) {
    lastPageHit = null;
    timer.reset(true);
  } else {
    /* TODO: THIS IS CAUSING A RACE CONDITION WHERE REDUX CANNOT UPDATE THE STORE PROPERLY IF THIS IS THE 
     LAST NETWORK CALL TO COMPLETE. WE CAN DISPATCH IT FROM AN ACTION OR DO SOMETHING ELSE FOR NETWORK ACTIONS LATER.
     DO NOT ADD BACK IN DIRECTLY!!! */
    // await logPageDuration();

    lastPageHit = {
      timestamp: Date.now(),
      pathname: location.pathname
    };
    timer.reset();
  }
  // if (location) {

  // }
});

if (history.location.pathname !== RouteDefinitions.login) {
  timer.reset();
}

const setProfile = function(token: string) {
  if (token) {
    store.dispatch(loginSuccess(token));
  }
};

if (config.environment === 'Local') {
  let jwtString: string = readCookie('token');
  setProfile(jwtString);
} else {
  authService.isLoggedIn().then(jwtToken => setProfile(jwtToken.jwt));
}

const app = (
  <Provider store={store}>
    <Router history={history}>
      <App />
    </Router>
  </Provider>
);

ReactDOM.render(app, document.getElementById('root'));
// registerServiceWorker();



// WEBPACK FOOTER //
// ./src/index.tsx